import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

interface TrackingCode {
  id: number;
  name: string;
  code: string;
  position: 'head' | 'body';
  isActive: boolean;
}

interface TrackingCodeInjectorProps {
  position: 'head' | 'body';
}

export default function TrackingCodeInjector({ position }: TrackingCodeInjectorProps) {
  const { data: trackingCodes = [], isLoading, error } = useQuery<TrackingCode[]>({
    queryKey: [`/api/tracking-codes/${position}`],
    queryFn: async () => {
      const response = await fetch(`/api/tracking-codes/${position}`);
      if (!response.ok) throw new Error('Failed to fetch tracking codes');
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Reduce retry attempts to fail fast
    retryDelay: 1000, // Shorter delay between retries
  });

  useEffect(() => {
    // Don't inject codes if still loading or if there's an error
    if (isLoading || error || trackingCodes.length === 0) return;

    trackingCodes.forEach((code) => {
      if (code.isActive) {
        try {
          // Validate the tracking code before processing
          if (!code.code || typeof code.code !== 'string') {
            console.warn(`TrackingCodeInjector: Invalid code for ${code.name}`);
            return;
          }

          // Fix domain configuration for Google Analytics and other tracking codes
          let processedCode = code.code.trim();
          
          // Extract JavaScript from HTML if present
          if (processedCode.includes('<script') || processedCode.includes('</script>')) {
            // Extract JavaScript content from script tags
            const scriptMatch = processedCode.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
            if (scriptMatch && scriptMatch[1]) {
              processedCode = scriptMatch[1].trim();
              // Validate the extracted code is valid JavaScript
              if (!processedCode || processedCode.includes('<') || processedCode.includes('>')) {
                console.warn(`TrackingCodeInjector: Invalid JavaScript extracted from ${code.name}, skipping`);
                return;
              }
              
              // Additional validation for JavaScript syntax
              try {
                // Test if the code can be parsed as JavaScript
                new Function(processedCode);
                console.log(`TrackingCodeInjector: Extracted JavaScript from ${code.name}`);
              } catch (syntaxError) {
                console.warn(`TrackingCodeInjector: Invalid JavaScript syntax in ${code.name}, skipping:`, syntaxError.message);
                return;
              }
            } else {
              console.warn(`TrackingCodeInjector: Code for ${code.name} contains script tags but no extractable content, skipping`);
              return;
            }
          }
          
          // Replace invalid domain configurations with correct domain
          processedCode = processedCode.replace(
            /domain\s*[:=]\s*['"](competitions|localhost|127\.0\.0\.1)['"]/gi,
            "domain='prizedrop.co.za'"
          );
          
          // Also fix any hardcoded domain references in cookie settings
          processedCode = processedCode.replace(
            /domain\s*[:=]\s*['"]\w+['"]/gi,
            "domain='prizedrop.co.za'"
          );

          // Handle noscript tags and iframes for GTM body code
          if (processedCode.includes('<noscript') || processedCode.includes('<iframe')) {
            if (position === 'body') {
              // For GTM body code, inject the noscript element directly
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = processedCode;
              const noscriptElement = tempDiv.querySelector('noscript');
              if (noscriptElement) {
                document.body.appendChild(noscriptElement);
                console.log(`TrackingCodeInjector: Injected noscript element for ${code.name}`);
                return;
              }
            }
          }
          
          // Additional validation - ensure the code doesn't have unexpected HTML
          if (processedCode.includes('<') && !processedCode.includes('gtag') && !processedCode.includes('dataLayer') && !processedCode.includes('iframe')) {
            console.warn(`TrackingCodeInjector: Code for ${code.name} contains unexpected HTML, skipping`);
            return;
          }

          // Create and inject script element
          if (processedCode && processedCode.trim()) {
            try {
              // Additional validation before injection
              if (processedCode.includes('<') && !processedCode.includes('gtag') && !processedCode.includes('dataLayer')) {
                console.warn(`TrackingCodeInjector: Code for ${code.name} contains HTML tags, skipping`);
                return;
              }
              
              const script = document.createElement('script');
              script.type = 'text/javascript';
              
              // Use script.text for better security and compatibility
              script.text = processedCode;
              
              if (position === 'head') {
                document.head.appendChild(script);
              } else {
                document.body.appendChild(script);
              }
            } catch (scriptError) {
              console.error(`TrackingCodeInjector: Error executing script for ${code.name}:`, scriptError);
              return;
            }
          }
          
          console.log(`TrackingCodeInjector: Injected ${code.name} into ${position} with domain fixed`);
        } catch (error) {
          console.error(`Error injecting tracking code ${code.name}:`, error);
        }
      }
    });
  }, [trackingCodes, position]);

  return null; // This component doesn't render anything visible
}